'use client';

import cn from 'clsx';
import { useCallback, useState, type TextareaHTMLAttributes } from 'react';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  error?: boolean;
  disabled?: boolean;
  showCounter?: boolean;
  textareaClassName?: string;
};

const formatNumber = (num: number) => num.toLocaleString('ko-KR');

const SCROLLBAR_WIDTH = 7;
const SCROLLBAR_MIN_THUMB_HEIGHT = 20;
const TEXTAREA_BOX_HEIGHT = '116px';

const TEXTAREA_BASE_CLASS =
  'body-medium-regular w-full resize-none bg-transparent text-neutral-foreground-main placeholder:text-neutral-foreground-disabled disabled:text-neutral-foreground-low disabled:placeholder:text-neutral-foreground-lowest outline-none overflow-y-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

const getTextareaLayout = (showCounter: boolean) =>
  showCounter
    ? {
        containerStyle: { height: TEXTAREA_BOX_HEIGHT },
        containerClassName: 'overflow-hidden',
        fieldClassName: 'min-h-0 flex-1',
        textareaStyle: { minHeight: 0, height: '100%' }
      }
    : {
        containerStyle: undefined,
        containerClassName: undefined,
        fieldClassName: undefined,
        textareaStyle: { minHeight: TEXTAREA_BOX_HEIGHT }
      };

export function Textarea({
  error,
  disabled,
  showCounter = false,
  maxLength,
  className,
  textareaClassName,
  style,
  value,
  defaultValue,
  onChange,
  ...props
}: TextareaProps) {
  const layout = getTextareaLayout(showCounter);
  const isControlled = value !== undefined;
  const [internalLength, setInternalLength] = useState(() => (typeof defaultValue === 'string' ? defaultValue.length : 0));
  const currentLength = isControlled ? (typeof value === 'string' ? value.length : 0) : internalLength;

  const [scrollInfo, setScrollInfo] = useState({ scrollTop: 0, scrollHeight: 0, clientHeight: 0 });

  const updateScrollInfo = useCallback((el: HTMLTextAreaElement) => {
    setScrollInfo({ scrollTop: el.scrollTop, scrollHeight: el.scrollHeight, clientHeight: el.clientHeight });
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    updateScrollInfo(e.currentTarget);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) setInternalLength(e.target.value.length);
    updateScrollInfo(e.currentTarget);
    onChange?.(e);
  };

  const handleTextareaRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      if (el) updateScrollInfo(el);
    },
    [updateScrollInfo]
  );

  const { scrollTop, scrollHeight, clientHeight } = scrollInfo;
  const isOverflow = scrollHeight > clientHeight;
  const thumbHeight = isOverflow ? Math.max((clientHeight / scrollHeight) * clientHeight, SCROLLBAR_MIN_THUMB_HEIGHT) : 0;
  const thumbTop = isOverflow ? (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight) : 0;

  return (
    <div
      className={cn(
        'flex flex-col rounded-bzc-small border-[1px] pl-normal-medium pr-0 py-normal-small caret-primary-foreground-main',
        {
          'bg-neutral-background-lowest': disabled,
          'bg-neutral-background-main': !disabled,
          'border-error-border-main focus-within:border-error-border-main focus-within:bg-neutral-background-main': error,
          'border-neutral-border-high focus-within:rounded-bzc-small focus-within:border-neutral-border-highest focus-within:bg-neutral-background-main':
            !error && !disabled
        },
        layout.containerClassName,
        className
      )}
      style={layout.containerStyle}
    >
      <div className={cn('relative flex pr-normal-medium', layout.fieldClassName)}>
        <textarea
          ref={handleTextareaRef}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onScroll={handleScroll}
          style={{ ...layout.textareaStyle, ...style }}
          className={cn(TEXTAREA_BASE_CLASS, textareaClassName)}
          {...props}
        />
        {isOverflow && (
          <div className="absolute right-0 top-0 bottom-0 flex justify-center" style={{ width: `${SCROLLBAR_WIDTH + 8}px` }}>
            <div className="relative w-full h-full">
              <div
                className="absolute rounded-[1000px] bg-neutral-background-high"
                style={{ width: `${SCROLLBAR_WIDTH}px`, height: `${thumbHeight}px`, top: `${thumbTop}px`, left: '50%', transform: 'translateX(-50%)' }}
              />
            </div>
          </div>
        )}
      </div>
      {showCounter && (
        <p className="caption-small-regular text-neutral-foreground-disabled text-right mt-tiny-medium pr-normal-medium">
          {maxLength !== undefined ? `${formatNumber(currentLength)}/${formatNumber(maxLength)}` : formatNumber(currentLength)}
        </p>
      )}
    </div>
  );
}
